const GID_PATTERN = /^gid:\/\/shopify\/([A-Za-z0-9_-]+)\/([0-9]+)$/

export const parseGid = (gid: string) => {
  const [, resource, idString] = gid.match(GID_PATTERN) ?? []
  if (!resource || !idString) {
    throw new Error(`Invalid GID: ${gid}`)
  }
  return {
    resource,
    id: Number(idString),
  }
}

declare global {
  interface Window {
    Shopify?: {
      routes: {
        root: string
      }
    }
  }
}

const getShopifyErrorMessage = (responseBody: string) => {
  if (!responseBody) return undefined

  try {
    const parsedBody: unknown = JSON.parse(responseBody)
    if (typeof parsedBody === 'object' && parsedBody !== null) {
      const { description, message } = parsedBody as Record<string, unknown>
      if (typeof description === 'string') return description
      if (typeof message === 'string') return message
    }
  } catch {
    // Shopify can return a plain-text response, so fall through to that value.
  }

  return responseBody.slice(0, 500)
}

export class ShopifyCartError extends Error {
  readonly status: number
  readonly statusText: string
  readonly shopifyMessage?: string

  constructor(response: Response, shopifyMessage?: string) {
    const responseDescription = shopifyMessage ?? response.statusText ?? `HTTP ${response.status}`
    super(`Failed to add product to cart: ${responseDescription}`)
    this.name = 'ShopifyCartError'
    this.status = response.status
    this.statusText = response.statusText
    this.shopifyMessage = shopifyMessage
  }
}

export const addVariantToCart = async (variantId: string) => {
  if (!window.Shopify) {
    throw new Error('Shopify is not loaded')
  }

  const formData = {
    items: [
      {
        id: variantId,
        quantity: 1,
      },
    ],
  }
  const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(formData),
  })

  if (!response.ok) {
    const responseBody = await response.text()
    throw new ShopifyCartError(response, getShopifyErrorMessage(responseBody))
  }

  return response.json()
}
